import {
  Flex,
  Progress,
  Card,
  useColorModeValue,
  Skeleton,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { GetLimitedPartnerFacilitiesForLenderQueryGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { MatchStatus } from './types';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';

type FacilitySummary = GetLimitedPartnerFacilitiesForLenderQueryGQLType['limitedPartnerFacilities'][number];

export const calculateMatchCounts = (facilities: FacilitySummary[]) => {
  let matched = 0;
  let notMatched = 0;
  facilities.forEach((facility) => {
    matched += facility.matchedCount;
    notMatched += facility.unmatchedCount;
  });
  return { matched, notMatched };
};

export const useMatchCounts = (facilities: FacilitySummary[]) => {
  const { matched, notMatched } = useMemo(() => {
    return calculateMatchCounts(facilities);
  }, [facilities]);
  return { matched, notMatched };
};

type StatusCardProps = {
  type: MatchStatus;
  mappingsTotal: number; // total number of mappings (matches and mismatches)
  mappingsCount: number; // number of mappings of the current type
  matchStatus: MatchStatus;
  updateMatchStatus: (statusCardType: MatchStatus) => void;
};
const StatusCard = ({
  type,
  mappingsTotal,
  mappingsCount,
  matchStatus,
  updateMatchStatus,
}: StatusCardProps) => {
  const borderColor = useColorModeValue('gray.500', 'gray.200');
  const bgHover = useColorModeValue('gray.50', 'gray.800');

  return (
    // !TODO @eldarshamukhamedov @design-system Replace with clickable `<Card />` variant
    <Card
      variant="outline"
      padding="16px"
      width="100%"
      _hover={{
        cursor: 'pointer',
        background: bgHover,
      }}
      borderColor={matchStatus === type ? borderColor : 'auto'}
      borderWidth={matchStatus === type ? '2px' : '1px'}
      onClick={() => updateMatchStatus(type)}
    >
      <Flex alignItems="center" gap="16px">
        <Icon
          icon={type === 'matched' ? 'circle-check' : 'circle-xmark'}
          color={type === 'matched' ? colors.green[400] : colors.orange[400]}
          size="large"
        />
        <Flex direction="column">
          <Typography variant="detail-muted">
            {commonUtils.toTitleCase(String(type))}
          </Typography>
          <Typography TextProps={{ mt: '8px' }} fontWeight="semibold">
            {mappingsCount}
          </Typography>
        </Flex>
      </Flex>
      <Progress
        mt="16px"
        value={
          mappingsCount === mappingsTotal // handle 0/0 case
            ? 100
            : (mappingsCount / mappingsTotal) * 100
        }
        colorScheme={type === 'matched' ? 'green' : 'orange'}
        size="sm"
        borderRadius="4px"
      />
    </Card>
  );
};

const MatchStatusCards = ({
  facilities,
  matchStatusForTables,
  setMatchStatusForTables,
}: {
  facilities: FacilitySummary[];
  matchStatusForTables: MatchStatus;
  setMatchStatusForTables: React.Dispatch<React.SetStateAction<MatchStatus>>;
}) => {
  const { matched, notMatched } = useMatchCounts(facilities);

  const updateMatchStatus = (statusCardType: MatchStatus) => {
    setMatchStatusForTables((prevStatus) => {
      if (prevStatus === statusCardType) {
        return 'all';
      }
      return statusCardType;
    });
  };

  return (
    <>
      <StatusCard
        type="notMatched"
        mappingsTotal={matched + notMatched}
        mappingsCount={notMatched}
        matchStatus={matchStatusForTables}
        updateMatchStatus={updateMatchStatus}
      />
      <StatusCard
        type="matched"
        mappingsTotal={matched + notMatched}
        mappingsCount={matched}
        matchStatus={matchStatusForTables}
        updateMatchStatus={updateMatchStatus}
      />
    </>
  );
};

export const MatchStatusCardsContainer = ({
  facilities,
  matchStatusForTables,
  setMatchStatusForTables,
}: {
  facilities: FacilitySummary[] | undefined;
  matchStatusForTables: MatchStatus;
  setMatchStatusForTables: React.Dispatch<React.SetStateAction<MatchStatus>>;
}) => {
  return (
    <Flex mt="2.5rem" gap="1.5rem" width="100%">
      {facilities ? (
        <MatchStatusCards
          facilities={facilities}
          matchStatusForTables={matchStatusForTables}
          setMatchStatusForTables={setMatchStatusForTables}
        />
      ) : (
        <>
          <Skeleton height="7rem" width="100%" />
          <Skeleton height="7rem" width="100%" />
        </>
      )}
    </Flex>
  );
};
